import { t } from "@lingui/macro";
import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const stats = [
  { value: 42000, suffix: "+", label: "Resumes Built" },
  { value: 94, suffix: "%", label: "ATS Pass Rate" },
  { value: 23, suffix: " min", label: "Avg. Build Time" },
];

const companyLogos = ["amazon", "google", "postman", "twilio", "zalando"];

const seenOnPlatforms = [
  { name: "TikTok", icon: "/brand-logos/tiktok.svg" },
  { name: "X", icon: "/brand-logos/x.svg" },
  { name: "YouTube", icon: "/brand-logos/youtube.svg" },
  { name: "Reddit", icon: "/brand-logos/reddit.svg" },
  { name: "LinkedIn", icon: "/brand-logos/linkedin.svg" },
];

const AnimatedCounter = ({
  value,
  suffix,
  label,
}: {
  value: number;
  suffix: string;
  label: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const duration = 1200;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      start = Math.round(eased * value);
      setDisplayValue(start);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [isInView, value]);

  return (
    <div ref={ref}>
      <div className="text-2xl font-bold tracking-tight sm:text-4xl">
        {displayValue.toLocaleString()}
        {suffix}
      </div>
      <div className="mt-1 text-sm text-foreground/60">{label}</div>
    </div>
  );
};

const CompanyLogo = ({ company }: { company: string }) => (
  <div className="col-span-1">
    <img
      className="block max-h-8 object-contain opacity-60 transition-opacity hover:opacity-100 dark:hidden sm:max-h-10"
      src={`/brand-logos/dark/${company}.svg`}
      alt={company}
      width={120}
      height={40}
    />
    <img
      className="hidden max-h-8 object-contain opacity-60 transition-opacity hover:opacity-100 dark:block sm:max-h-10"
      src={`/brand-logos/light/${company}.svg`}
      alt={company}
      width={120}
      height={40}
    />
  </div>
);

export const SocialProofSection = () => (
  <section
    id="social-proof"
    aria-label="Social proof and statistics"
    className="relative border-y border-border/40 bg-secondary-accent/50 py-16"
  >
    <div className="container">
      <div className="grid grid-cols-1 gap-8 text-center sm:grid-cols-3">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0, transition: { delay: i * 0.1 } }}
          >
            <AnimatedCounter value={stat.value} suffix={stat.suffix} label={stat.label} />
          </motion.div>
        ))}
      </div>

      <div className="mt-12 space-y-6">
        <p className="text-center text-sm text-foreground/50">
          {t`People using CareerBldr have landed jobs at these companies`}
        </p>
        <div className="mx-auto grid max-w-3xl grid-cols-3 items-center gap-x-6 gap-y-4 sm:grid-cols-5 sm:gap-x-8">
          {companyLogos.map((company) => (
            <CompanyLogo key={company} company={company} />
          ))}
        </div>
      </div>

      {seenOnPlatforms.length > 0 && (
        <div className="mt-10 flex flex-wrap items-center justify-center gap-5 sm:gap-10">
          <span className="text-xs font-medium uppercase tracking-wider text-foreground/40">
            {t`As seen on`}
          </span>
          {seenOnPlatforms.map((platform) => (
            <img
              key={platform.name}
              src={platform.icon}
              alt={platform.name}
              className="h-8 w-8 opacity-50 transition-opacity hover:opacity-80 sm:h-9 sm:w-9"
            />
          ))}
        </div>
      )}
    </div>
  </section>
);
